import '@/styles/wheel-picker.css';
import * as React from 'react';
import { WheelPicker, WheelPickerWrapper, type WheelPickerOption } from '@ncdai/react-wheel-picker';
import { Clock } from 'lucide-react';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import type { TimeEntryBinding, TimeEntryState } from '@/hooks/useTimeEntry';
import { cn } from '@/lib/utils';

// ─── 12h helpers ───────────────────────────────────────────────────────────

type Period = 'AM' | 'PM';

function to12(hour24: number): { h12: number; display: string; period: Period } {
  const period: Period = hour24 >= 12 ? 'PM' : 'AM';
  let h = hour24 % 12;
  if (h === 0) h = 12;
  return { h12: h, display: String(h).padStart(2, '0'), period };
}

function to24(h12: number, period: Period): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

function formatDisplay(hour24: number, minute: number): string {
  const { display, period } = to12(hour24);
  return `${display}:${String(minute).padStart(2, '0')} ${period}`;
}

/** Accept explicit AM/PM or a 24-hour clock; never coerce an invalid time. */
function parseTypedTime(text: string): string | null {
  const match = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i.exec(text.trim());
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] ?? 0);
  if (minute > 59) return null;
  if (match[3]) {
    if (hour < 1 || hour > 12) return null;
    hour = to24(hour, match[3].toUpperCase() as Period);
  } else if (hour > 23) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Text that a time can still grow out of as it is typed: "8", "8:", "8:1",
 * "8:15 a", "14:30". Whatever no more typing could turn into a time the field
 * accepts ("ssa", "8:150", "08:00 AMx") is refused as it is typed.
 */
const TYPED_TIME_PREFIX = /^(\d{1,2}(:\d?|(:\d{2})?\s*([ap]m?)?))?$/i;

/** "16:15" → "4:15 PM": how a person would type it, no leading zero. */
function spokenTime(value: string): string {
  const [hour, minute] = value.split(':').map(Number);
  const { h12, period } = to12(hour);
  return `${h12}:${String(minute).padStart(2, '0')} ${period}`;
}

/**
 * What to say about typed text the field cannot take, in terms of the times it
 * offers. Those are the chosen arrival window in quarter hours, so a 4–8 PM
 * visit is shown "4:15 PM or 20:00", never a morning time it would refuse. A
 * field open all day keeps the general examples.
 */
function typedTimeMessages(allowedTimes: string[]): { unreadable: string; outside: string } {
  const first = allowedTimes[0];
  const last = allowedTimes[allowedTimes.length - 1];
  if (!first || !last || allowedTimes.length >= 96) {
    return {
      unreadable: 'Enter a time such as 8:15 AM or 14:30.',
      outside: 'Choose a time available in the picker.',
    };
  }
  return {
    unreadable: `Enter a time such as ${spokenTime(allowedTimes[1] ?? first)} or ${last}.`,
    outside: `Choose a time between ${spokenTime(first)} and ${spokenTime(last)}.`,
  };
}

function displayTime(value: string): string {
  if (!value) return '';
  const [hour, minute] = value.split(':').map(Number);
  return formatDisplay(hour, minute);
}

// ─── Wheel picker options ──────────────────────────────────────────────────

const hourOptions: WheelPickerOption[] = Array.from({ length: 12 }, (_, i) => {
  const v = String(i + 1);
  return { label: v.padStart(2, '0'), value: v };
});

const minuteOptions: WheelPickerOption[] = Array.from({ length: 60 }, (_, i) => {
  const v = String(i);
  return { label: v.padStart(2, '0'), value: v };
});

const meridiemOptions: WheelPickerOption[] = [
  { label: 'AM', value: 'AM' },
  { label: 'PM', value: 'PM' },
];

const wheelClasses = {
  optionItem: 'text-muted-foreground tabular-nums text-sm',
  highlightItem: 'text-foreground tabular-nums text-sm font-medium',
};

// ─── Desktop: segment inputs ───────────────────────────────────────────────

interface SegmentProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value: string;
  min: number;
  max: number;
  onValueChange: (v: string) => void;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
}

const SegmentInput = React.forwardRef<HTMLInputElement, SegmentProps>(
  ({ className, value, min, max, onValueChange, onLeftFocus, onRightFocus, ...props }, ref) => {
    const [flag, setFlag] = React.useState(false);

    React.useEffect(() => {
      if (flag) {
        const timer = setTimeout(() => setFlag(false), 2000);
        return () => clearTimeout(timer);
      }
    }, [flag]);

    const clamp = (v: string) => {
      let n = parseInt(v, 10);
      if (isNaN(n)) return String(min).padStart(2, '0');
      if (n > max) n = min;
      if (n < min) n = max;
      return String(n).padStart(2, '0');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') return;
      e.preventDefault();
      if (e.key === 'ArrowRight') onRightFocus?.();
      if (e.key === 'ArrowLeft') onLeftFocus?.();
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const step = e.key === 'ArrowUp' ? 1 : -1;
        let n = parseInt(value, 10) + step;
        if (n > max) n = min;
        if (n < min) n = max;
        if (flag) setFlag(false);
        onValueChange(String(n).padStart(2, '0'));
      }
      if (e.key >= '0' && e.key <= '9') {
        const newValue = !flag ? '0' + e.key : value.slice(1, 2) + e.key;
        if (flag) onRightFocus?.();
        setFlag((prev) => !prev);
        onValueChange(clamp(newValue));
      }
    };

    return (
      <Input
        ref={ref}
        className={cn(
          'w-[40px] h-8 text-center font-mono text-xs tabular-nums caret-transparent focus:bg-accent focus:text-accent-foreground [&::-webkit-inner-spin-button]:appearance-none px-1',
          className,
        )}
        value={value}
        onChange={(e) => e.preventDefault()}
        type="tel"
        inputMode="decimal"
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  },
);
SegmentInput.displayName = 'SegmentInput';

// ─── Public: TimePicker ────────────────────────────────────────────────────

/** Why a form will not save while its start time or duration shows a value it refused. */
export const UNACCEPTED_TIME_ENTRY_MESSAGE =
  'Correct the start time or duration shown in red before saving.';

interface BaseTimePickerProps {
  id?: string;
  /** Time string in "HH:mm" 24h format */
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

interface RestrictedTimePickerProps extends BaseTimePickerProps {
  /** Restrict the clock wheels, and what may be typed, to these HH:mm values. */
  allowedTimes: string[];
  /**
   * Typed text only becomes `value` once it is taken (Enter, blur, or the
   * form's `commit()`), and a refused time stays on screen with its error
   * while `value` keeps the last time taken. Until then the field and the form
   * disagree, and a form that saved would send a time nobody is looking at
   * (NOT-781). Through this binding (from `useTimeEntry`) the field reports
   * that — `typing` or `refused`, `null` once taken, reverted, replaced or
   * gone — and lets the form's Save take the typed text first. Required, so no
   * form can mount the field and miss it.
   */
  entry: TimeEntryBinding;
}

type TimePickerProps =
  | (BaseTimePickerProps & { allowedTimes?: undefined; entry?: undefined })
  | RestrictedTimePickerProps;

export function TimePicker(props: TimePickerProps) {
  const { value, onChange, className, id } = props;
  const isDesktop = useIsDesktop();
  const [open, setOpen] = React.useState(false);

  const [rawH = '0', rawM = '0'] = value.split(':');
  const hour24 = parseInt(rawH, 10);
  const minuteNum = parseInt(rawM, 10);
  const { h12, display: h12Display, period } = to12(hour24);
  const minuteDisplay = String(minuteNum).padStart(2, '0');

  const emit = React.useCallback(
    (newH12: number | string, newMin: number | string, newPeriod: Period) => {
      const h24 = to24(typeof newH12 === 'string' ? parseInt(newH12, 10) : newH12, newPeriod);
      const m =
        typeof newMin === 'string' ? newMin.padStart(2, '0') : String(newMin).padStart(2, '0');
      onChange(`${String(h24).padStart(2, '0')}:${m}`);
    },
    [onChange],
  );

  if (props.allowedTimes) {
    return (
      <RestrictedTimePicker
        id={id}
        value={value}
        onChange={onChange}
        className={className}
        allowedTimes={props.allowedTimes}
        entry={props.entry}
      />
    );
  }

  // ── Desktop: inline segment inputs ──
  if (isDesktop) {
    return (
      <DesktopTimePicker
        h12Display={h12Display}
        minuteDisplay={minuteDisplay}
        period={period}
        emit={emit}
        className={className}
      />
    );
  }

  // ── Mobile: popover with wheel picker ──
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-8 gap-1.5 px-2.5 font-mono text-xs tabular-nums', className)}
        >
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDisplay(hour24, minuteNum)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <WheelPickerWrapper className="h-[180px] gap-0.5">
          <WheelPicker
            options={hourOptions}
            value={String(h12)}
            onValueChange={(v) => {
              const next = parseInt(String(v), 10);
              const crossed = (h12 === 11 && next === 12) || (h12 === 12 && next === 11);
              emit(v, minuteNum, crossed ? (period === 'AM' ? 'PM' : 'AM') : period);
            }}
            infinite
            classNames={{
              ...wheelClasses,
              highlightWrapper: 'bg-accent rounded-l-md',
            }}
          />
          <WheelPicker
            options={minuteOptions}
            value={String(minuteNum)}
            onValueChange={(v) => emit(h12, v, period)}
            infinite
            classNames={{
              ...wheelClasses,
              highlightWrapper: 'bg-accent',
            }}
          />
          <WheelPicker
            options={meridiemOptions}
            value={period}
            onValueChange={(v) => emit(h12, minuteNum, v as Period)}
            classNames={{
              ...wheelClasses,
              highlightWrapper: 'bg-accent rounded-r-md',
            }}
          />
        </WheelPickerWrapper>
      </PopoverContent>
    </Popover>
  );
}

// ── Desktop sub-component ──────────────────────────────────────────────────

function DesktopTimePicker({
  h12Display,
  minuteDisplay,
  period,
  emit,
  className,
}: {
  h12Display: string;
  minuteDisplay: string;
  period: Period;
  emit: (h12: number | string, min: number | string, period: Period) => void;
  className?: string;
}) {
  const hourRef = React.useRef<HTMLInputElement>(null);
  const minuteRef = React.useRef<HTMLInputElement>(null);

  // A real clock's hour progression crosses the meridiem at the 11↔12
  // boundary: 11 AM → 12 PM → 1 PM … 11 PM → 12 AM. Stepping (or typing)
  // across that boundary flips AM/PM instead of landing on the wrong half of
  // the day (the old behavior turned "11 AM + 1" into 12 AM = midnight).
  const handleHourChange = (v: number | string) => {
    const prev = parseInt(h12Display, 10);
    const next = typeof v === 'string' ? parseInt(v, 10) : v;
    const crossed = (prev === 11 && next === 12) || (prev === 12 && next === 11);
    emit(v, minuteDisplay, crossed ? (period === 'AM' ? 'PM' : 'AM') : period);
  };

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      <SegmentInput
        ref={hourRef}
        value={h12Display}
        min={1}
        max={12}
        onValueChange={handleHourChange}
        onRightFocus={() => minuteRef.current?.focus()}
      />
      <span className="text-xs text-muted-foreground font-mono">:</span>
      <SegmentInput
        ref={minuteRef}
        value={minuteDisplay}
        min={0}
        max={59}
        onValueChange={(v) => emit(h12Display, v, period)}
        onLeftFocus={() => hourRef.current?.focus()}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-10 px-0 font-mono text-xs"
        onClick={() => emit(h12Display, minuteDisplay, period === 'AM' ? 'PM' : 'AM')}
      >
        {period}
      </Button>
    </div>
  );
}

/** The same clock wheels, with each column restricted to valid arrival times. */
function RestrictedTimePicker({
  id,
  value,
  onChange,
  className,
  allowedTimes,
  entry,
}: RestrictedTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const optionsKey = allowedTimes.join(',');
  const committedDisplay = allowedTimes.includes(value) ? displayTime(value) : '';
  const [text, setText] = React.useState(committedDisplay);
  const [error, setError] = React.useState('');
  // Why the last key did nothing. The key changed nothing, so the field still
  // holds what it held, and is not marked invalid for it: that would show an
  // error beside a time the form will save.
  const [hint, setHint] = React.useState('');
  const errorId = React.useId();
  const messages = typedTimeMessages(allowedTimes);
  // The newest binding, for the effects below: a parent's fresh object must
  // not re-run them (and reset the field) on every render.
  const binding = React.useRef(entry);
  React.useLayoutEffect(() => {
    binding.current = entry;
  });
  // Typed text is not the form's value until it is taken, so the form is told
  // while there is any: still being typed, or refused. Reported in the event
  // that changes it, never an effect later — pressing Save blurs this field
  // first, and that click must find the form in step with what it shows.
  const settle = (state: TimeEntryState) => binding.current.report(state);
  React.useEffect(() => {
    setText(committedDisplay);
    setError('');
    setHint('');
    binding.current.report(null);
  }, [committedDisplay, optionsKey]);
  // An entry that leaves the screen is no longer anything to correct.
  React.useEffect(() => () => binding.current.report(null), []);
  /** Take the typed text: the time now shown, or `null` when it is refused. */
  const commitText = (): string | null => {
    setHint('');
    // Nothing typed: the time on screen — none, when `value` is not one this
    // field offers and it shows blank.
    if (text === committedDisplay) return committedDisplay ? value : '';
    const parsed = parseTypedTime(text);
    if (!parsed || !allowedTimes.includes(parsed)) {
      setError(parsed ? messages.outside : messages.unreadable);
      settle('refused');
      return null;
    }
    setError('');
    setText(displayTime(parsed));
    settle(null);
    if (parsed !== value) onChange(parsed);
    return parsed;
  };
  // The form's Save takes the typed text through this, as Enter would. A tap
  // on Save does not blur the field on every touch browser, and one click must
  // save what is on screen whether or not it did.
  React.useLayoutEffect(() => {
    binding.current.commitRef.current = commitText;
  });
  React.useEffect(
    () => () => {
      binding.current.commitRef.current = null;
    },
    [],
  );
  const displayed = open ? draft : value;
  const selected = allowedTimes.includes(displayed) ? displayed : allowedTimes[0];
  const [hour, minute] = (selected ?? '00:00').split(':').map(Number);
  const period = to12(hour).period;
  const hours = Array.from(new Set(allowedTimes.map((time) => Number(time.slice(0, 2)))));
  const periods = Array.from(new Set(hours.map((h) => to12(h).period)));
  const minutes = allowedTimes
    .filter((time) => Number(time.slice(0, 2)) === hour)
    .map((time) => Number(time.slice(3)));
  // Wheel momentum can emit an option from the previous hour/period after
  // its options change. Validate the complete time, not just each column.
  const emitAllowed = (next: string) => {
    if (allowedTimes.includes(next)) setDraft(next);
  };
  const changeHour = (nextHour: number) => {
    const candidates = allowedTimes.filter((time) => Number(time.slice(0, 2)) === nextHour);
    const sameMinute = candidates.find((time) => Number(time.slice(3)) === minute);
    if (sameMinute || candidates[0]) emitAllowed(sameMinute ?? candidates[0]!);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setDraft(value);
        else if (allowedTimes.includes(draft)) {
          setText(displayTime(draft));
          setError('');
          setHint('');
          settle(null);
          if (draft !== value) onChange(draft);
        }
        setOpen(nextOpen);
      }}
    >
      <div className={cn('relative w-full', className)}>
        <Input
          id={id}
          aria-label={id ? undefined : 'Start time'}
          value={text}
          placeholder="Select start time"
          disabled={!allowedTimes.length}
          className="h-9 pr-10"
          aria-invalid={Boolean(error)}
          aria-describedby={error || hint ? errorId : undefined}
          onChange={(event) => {
            if (!TYPED_TIME_PREFIX.test(event.target.value)) {
              setHint(messages.unreadable);
              return;
            }
            setText(event.target.value);
            setError('');
            setHint('');
            settle(event.target.value !== committedDisplay ? 'typing' : null);
          }}
          onBlur={() => void commitText()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitText();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setText(committedDisplay);
              setError('');
              setHint('');
              settle(null);
            }
          }}
        />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Choose start time"
            disabled={!selected}
            className="absolute right-0 top-0 h-9 w-9"
          >
            <Clock className="size-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        {error ? (
          <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
            {error}
          </p>
        ) : hint ? (
          <p id={errorId} role="status" className="mt-1 text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
      <PopoverContent className="w-56 p-2" align="start">
        <WheelPickerWrapper className="h-[180px] gap-0.5">
          <WheelPicker
            key={`hours-${period}-${optionsKey}`}
            options={hours
              .filter((h) => to12(h).period === period)
              .map((h) => ({ value: String(h), label: to12(h).display }))}
            value={String(hour)}
            onValueChange={(v) => changeHour(Number(v))}
            classNames={{ ...wheelClasses, highlightWrapper: 'bg-accent rounded-l-md' }}
          />
          <WheelPicker
            key={`minutes-${hour}-${optionsKey}`}
            options={minutes.map((m) => ({ value: String(m), label: String(m).padStart(2, '0') }))}
            value={String(minute)}
            onValueChange={(v) =>
              emitAllowed(`${String(hour).padStart(2, '0')}:${String(v).padStart(2, '0')}`)
            }
            classNames={{ ...wheelClasses, highlightWrapper: 'bg-accent' }}
          />
          <WheelPicker
            key={`periods-${optionsKey}`}
            options={periods.map((p) => ({ value: p, label: p }))}
            value={period}
            onValueChange={(v) => {
              const target = to24(to12(hour).h12, v as Period);
              const nextHour = hours.includes(target)
                ? target
                : hours.find((h) => to12(h).period === v);
              if (nextHour != null) changeHour(nextHour);
            }}
            classNames={{ ...wheelClasses, highlightWrapper: 'bg-accent rounded-r-md' }}
          />
        </WheelPickerWrapper>
      </PopoverContent>
    </Popover>
  );
}
